import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import {
  PATH_RESOLVABLE_SERVICE_MATERIALS,
  PATH_RESOLVABLE_SERVICE_MATERIALS_EDIT,
} from '../../../app/constants/paths.constant';
import { RightDrawer } from '../../../components/RightDrawer';
import { SpeedDial } from '../../../components/SpeedDial';
import { FormVariant } from '../../../forms';
import {
  ResolvableServiceForm,
  ResolvableServiceFormTab,
} from '../../../forms/ResolvableService';
import { useBuildPaths } from '../../../hooks/useBuildPaths.hook';

import { List } from './List';

export const ResolvableServicePage_Materials_ResolvableServiceBaseInterface_Fragment =
  graphql(`
    fragment ResolvableServicePage_Materials_ResolvableServiceBaseInterface_Fragment on ResolvableServiceBaseInterface {
      _id
      materialUsages {
        ...ResolvableServicePage_MaterialUsagesList_MaterialUsage_Fragment
      }
      ...ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment
    }
  `);

interface Props {
  readonly resolvableService: FragmentType<
    typeof ResolvableServicePage_Materials_ResolvableServiceBaseInterface_Fragment
  >;
}

export const Materials = ({
  resolvableService: pResolvableService,
}: Readonly<Props>) => {
  const resolvableService = getFragmentData(
    ResolvableServicePage_Materials_ResolvableServiceBaseInterface_Fragment,
    pResolvableService,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_SERVICE_MATERIALS,
      edit: PATH_RESOLVABLE_SERVICE_MATERIALS_EDIT,
    },
    { id: resolvableService._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <List materialUsages={resolvableService.materialUsages} />
      <SpeedDial
        actions={[
          {
            id: 'editResolvableServiceMaterials',
            icon: <EditIcon />,
            name: 'Modifier Matériels Liés',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Matériels Liés au Service"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <ResolvableServiceForm
          formVariant={FormVariant.Modification}
          initialValues={resolvableService}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          resolvableServiceId={resolvableService._id}
          stepIdsSubset={[
            ResolvableServiceFormTab.MaterialsSelection,
            ResolvableServiceFormTab.MaterialsOrder,
            ResolvableServiceFormTab.MaterialsUsage,
          ]}
          title="Formulaire de modification des Matériels Liés au Service"
        />
      </RightDrawer>
    </>
  );
};
